import type { Org, UserProfile } from '@athwab/shared';
import { useQueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
} from 'react';
import { WORKSPACE_FEATURE_ENABLED } from '../../config';
import { identifyAnalyticsUser, resetAnalyticsUser } from '../../lib/analytics';
import { API_BASE_URL, apiFetch } from '../../lib/api';
import { getFirebaseAuth } from '../../lib/firebase';
import { useAssistantStore } from '../assistant/store/useAssistantStore';
import { useWorkspaceStore } from '../workspace/store/useWorkspaceStore';
import { authManager, type AuthSnapshot } from './auth-manager';
import { clearUserScopedQueryState } from './auth-query-cache';
import {
  clearGoogleRedirectPending,
  EMAIL_STORAGE_KEY,
  markGoogleRedirectPending,
  REDIRECT_STORAGE_KEY,
  sanitizeRedirect,
} from './auth-storage';
import { shouldFallbackToGoogleRedirect, shouldUseGooglePopupFirst } from './google-auth';

export interface AuthContextValue extends AuthSnapshot {
  isLoading: boolean;
  sendMagicLink: (email: string, options?: { redirect?: string }) => Promise<void>;
  completeMagicLinkSignIn: (email?: string) => Promise<{ redirect?: string } | null>;
  signInWithGoogle: (options?: { redirect?: string }) => Promise<void>;
  signInWithDevSecret: (
    secret: string,
    options?: { redirect?: string }
  ) => Promise<string | undefined>;
  signInWithDevEmail: (
    email: string,
    options?: { redirect?: string }
  ) => Promise<string | undefined>;
  devLoginEnabled: boolean;
  devEmailLoginEnabled: boolean;
  refreshUser: () => Promise<void>;
  signOut: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | undefined>(undefined);
const DEV_LOGIN_ENABLED =
  !import.meta.env.PROD &&
  (import.meta.env.VITE_ENABLE_DEV_ADMIN_LOGIN ?? 'false').toLowerCase() === 'true';
const DEV_EMAIL_LOGIN_ENABLED =
  !import.meta.env.PROD &&
  (
    import.meta.env.VITE_ENABLE_DEV_EMAIL_LOGIN ??
    import.meta.env.VITE_ENABLE_DEV_ADMIN_LOGIN ??
    'false'
  ).toLowerCase() === 'true';
const DEV_SESSION_LOGIN_ENABLED = DEV_LOGIN_ENABLED || DEV_EMAIL_LOGIN_ENABLED;

const mapGoogleAuthError = (error: unknown) => {
  if (
    error &&
    typeof error === 'object' &&
    'code' in error &&
    typeof (error as { code?: unknown }).code === 'string'
  ) {
    const code = (error as { code: string }).code;
    if (code === 'auth/popup-blocked') {
      return new Error('Google sign-in was blocked by the browser. Try again in the same tab.');
    }
    if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
      return new Error('Google sign-in was canceled before it finished.');
    }
    if (code === 'auth/web-storage-unsupported' || code === 'auth/web-storage-unavailable') {
      return new Error(
        'This browser blocked the storage needed to finish Google sign-in. Try another browser or disable private browsing.'
      );
    }
    if (code === 'auth/unauthorized-domain') {
      return new Error('Google sign-in is not enabled for this domain yet.');
    }
  }

  return error instanceof Error ? error : new Error('Google sign-in failed. Please try again.');
};

const cleanupAuthQuery = () => {
  if (typeof window === 'undefined') {
    return;
  }

  const url = new URL(window.location.href);
  url.searchParams.delete('apiKey');
  url.searchParams.delete('lang');
  url.searchParams.delete('mode');
  url.searchParams.delete('oobCode');
  url.searchParams.delete('continueUrl');
  window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
};

class HttpError extends Error {
  status: number;
  code?: string;
  messageAr?: string;
  closureUrl?: string;

  constructor(
    status: number,
    payload: {
      message: string;
      code?: string;
      messageAr?: string;
      closureUrl?: string;
    }
  ) {
    super(payload.message);
    this.status = status;
    this.code = payload.code;
    this.messageAr = payload.messageAr;
    this.closureUrl = payload.closureUrl;
  }
}

const isRegistrationClosedError = (error: unknown) =>
  error instanceof HttpError && error.code === 'SHUTDOWN_REGISTRATIONS_DISABLED';

const parseHttpError = async (response: Response) => {
  const fallbackMessage = `Request failed with status ${response.status}`;
  let payload: {
    message: string;
    code?: string;
    messageAr?: string;
    closureUrl?: string;
  } = { message: fallbackMessage };

  try {
    const text = await response.text();
    if (text) {
      try {
        const data = JSON.parse(text) as {
          message?: unknown;
          details?: unknown;
          code?: unknown;
          messageAr?: unknown;
          closureUrl?: unknown;
        };
        if (data?.message && typeof data.message === 'string') {
          payload = {
            message:
              data.details !== undefined
                ? JSON.stringify({ message: data.message, details: data.details })
                : data.message,
            code: typeof data.code === 'string' ? data.code : undefined,
            messageAr: typeof data.messageAr === 'string' ? data.messageAr : undefined,
            closureUrl: typeof data.closureUrl === 'string' ? data.closureUrl : undefined,
          };
        } else {
          payload = { message: text };
        }
      } catch {
        payload = { message: text };
      }
    }
  } catch {
    // Ignore parsing failures.
  }

  return new HttpError(response.status, payload);
};

const parseUserResponse = async (response: Response): Promise<UserProfile> => {
  if (!response.ok) {
    throw await parseHttpError(response);
  }

  const payload = (await response.json()) as { user: UserProfile };
  if (!payload || typeof payload !== 'object' || !payload.user) {
    throw new Error('Malformed user response.');
  }

  return payload.user;
};

const fetchCurrentUserWithToken = async (token: string): Promise<UserProfile> => {
  const response = await fetch(`${API_BASE_URL}/auth/me`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    credentials: 'include',
  });

  return parseUserResponse(response);
};

const createSessionWithToken = async (token: string): Promise<UserProfile> => {
  const response = await fetch(`${API_BASE_URL}/auth/session`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    credentials: 'include',
  });

  if (!response.ok) {
    throw await parseHttpError(response);
  }

  if (response.status === 204) {
    throw new Error('Session response did not include the authenticated user.');
  }

  return parseUserResponse(response);
};

const fetchCurrentUserFromSession = async (): Promise<UserProfile> => {
  const response = await fetch(`${API_BASE_URL}/auth/me`, {
    credentials: 'include',
  });

  return parseUserResponse(response);
};

const buildActionLinkUrl = (redirect?: string) => {
  if (typeof window === 'undefined') {
    return '';
  }

  const base = import.meta.env.VITE_WEB_URL ?? window.location.origin;
  const url = new URL('/', base);

  if (redirect) {
    url.searchParams.set('redirect', redirect);
  }

  return url.toString();
};

let authBootstrapPromise: Promise<void> | null = null;

const initializeAuthManager = async () => {
  try {
    const [{ getRedirectResult, onIdTokenChanged, signOut: firebaseSignOut }, auth] =
      await Promise.all([import('firebase/auth'), getFirebaseAuth()]);

    let redirectError: Error | null = null;
    try {
      await getRedirectResult(auth);
      cleanupAuthQuery();
    } catch (error) {
      cleanupAuthQuery();
      redirectError = mapGoogleAuthError(error);
    }

    onIdTokenChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        if (!DEV_SESSION_LOGIN_ENABLED) {
          clearGoogleRedirectPending();
          authManager.setSnapshot({
            status: 'unauthenticated',
            user: null,
            error: redirectError?.message,
          });
          redirectError = null;
          return;
        }

        const previous = authManager.getSnapshot();
        authManager.setSnapshot({
          status: 'loading',
          user: previous.user,
          error: undefined,
        });

        try {
          const profile = await fetchCurrentUserFromSession();
          clearGoogleRedirectPending();
          authManager.setSnapshot({
            status: 'authenticated',
            user: profile,
            error: undefined,
          });
        } catch (error) {
          const parsedError =
            error instanceof Error ? error : new Error('Failed to resolve development session');
          if (
            parsedError instanceof HttpError &&
            (parsedError.status === 401 || parsedError.status === 403)
          ) {
            clearGoogleRedirectPending();
            authManager.setSnapshot({
              status: 'unauthenticated',
              user: null,
              error: undefined,
            });
            return;
          }
          console.error('Failed to resolve development session', parsedError);
          clearGoogleRedirectPending();
          authManager.setSnapshot({
            status: 'unauthenticated',
            user: null,
            error: parsedError.message,
          });
        }
        return;
      }

      const previous = authManager.getSnapshot();
      authManager.setSnapshot({
        status: 'loading',
        user: previous.user,
        error: undefined,
      });

      try {
        const token = await firebaseUser.getIdToken();
        let profile: UserProfile;
        try {
          profile = await createSessionWithToken(token);
        } catch (error) {
          if (isRegistrationClosedError(error)) {
            throw error;
          }
          profile = await fetchCurrentUserWithToken(token);
        }
        clearGoogleRedirectPending();
        authManager.setSnapshot({
          status: 'authenticated',
          user: profile,
          error: undefined,
        });
        redirectError = null;
      } catch (error) {
        const parsedError = mapGoogleAuthError(error);
        console.error('Failed to resolve authenticated session', parsedError);
        await firebaseSignOut(auth).catch(() => undefined);
        clearGoogleRedirectPending();
        authManager.setSnapshot({
          status: 'unauthenticated',
          user: null,
          error: parsedError.message,
          errorCode: parsedError instanceof HttpError ? parsedError.code : undefined,
        });
      }
    });
  } catch (error) {
    const parsedError =
      error instanceof Error ? error : new Error('Failed to initialize Firebase auth');
    console.error('Failed to initialize Firebase auth', parsedError);

    try {
      const profile = await fetchCurrentUserFromSession();
      authManager.setSnapshot({
        status: 'authenticated',
        user: profile,
        error: undefined,
      });
    } catch (sessionError) {
      const parsedSessionError =
        sessionError instanceof Error
          ? sessionError
          : new Error('Failed to resolve fallback session after Firebase init failure');

      if (
        parsedSessionError instanceof HttpError &&
        (parsedSessionError.status === 401 || parsedSessionError.status === 403)
      ) {
        clearGoogleRedirectPending();
        authManager.setSnapshot({
          status: 'unauthenticated',
          user: null,
          error: undefined,
        });
        return;
      }

      authManager.setSnapshot({
        status: 'unauthenticated',
        user: null,
        error: parsedError.message,
      });
      clearGoogleRedirectPending();
    }
  }
};

export const ensureAuthResolved = async (): Promise<AuthSnapshot> => {
  if (!authBootstrapPromise) {
    authBootstrapPromise = initializeAuthManager().catch((error) => {
      authBootstrapPromise = null;
      throw error;
    });
  }

  await authBootstrapPromise;
  return authManager.waitForResolved();
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const snapshot = useSyncExternalStore<AuthSnapshot>(
    authManager.subscribe,
    authManager.getSnapshot,
    authManager.getSnapshot
  );
  const workspaceStore = useWorkspaceStore;
  const queryClient = useQueryClient();
  const previousUserId = useRef<string | null>(null);

  useEffect(() => {
    void ensureAuthResolved();
  }, []);

  useEffect(() => {
    const currentUserId = snapshot.user?.id ?? null;
    if (currentUserId !== previousUserId.current) {
      clearUserScopedQueryState(queryClient);
      useAssistantStore.getState().reset();
      workspaceStore.getState().reset();
      previousUserId.current = currentUserId;
      if (snapshot.user) {
        identifyAnalyticsUser(snapshot.user);
      } else {
        resetAnalyticsUser();
      }
    }
  }, [queryClient, snapshot.user, snapshot.user?.id, workspaceStore]);

  useEffect(() => {
    const bootstrapWorkspace = async () => {
      if (!WORKSPACE_FEATURE_ENABLED || snapshot.status !== 'authenticated') return;
      try {
        const normalizeOrg = (org: Org | (Org & { _id?: string })) => ({
          ...org,
          id: org.id ?? (org as { _id?: string })._id ?? '',
        });

        const response = await apiFetch<{ orgs: { org: Org }[] | Org[] }>('/orgs');
        const orgs = (
          Array.isArray(response.orgs)
            ? response.orgs.map((entry) =>
                'org' in entry ? normalizeOrg(entry.org) : normalizeOrg(entry as Org)
              )
            : []
        ).filter((org) => org.id);

        if (!orgs.length) return;

        const state = workspaceStore.getState();
        const currentOrg = orgs.find((org) => org.id === state.orgId);
        const nextOrg = currentOrg ?? orgs[0];

        const orgChanged = !state.orgId || !currentOrg;

        if (orgChanged) {
          workspaceStore.getState().setOrgId(nextOrg.id);
        }

        if ((orgChanged || !state.clientId) && nextOrg.defaultClientId) {
          workspaceStore.getState().setClientId(nextOrg.defaultClientId);
        }
      } catch {
        // ignore bootstrap failures; user can select manually
      }
    };
    void bootstrapWorkspace();
  }, [snapshot.status, workspaceStore]);

  const sendMagicLink = useCallback(async (email: string, options?: { redirect?: string }) => {
    const redirect = sanitizeRedirect(options?.redirect);

    if (typeof window !== 'undefined') {
      window.localStorage.setItem(EMAIL_STORAGE_KEY, email);

      if (redirect) {
        window.localStorage.setItem(REDIRECT_STORAGE_KEY, redirect);
      } else {
        window.localStorage.removeItem(REDIRECT_STORAGE_KEY);
      }
    }

    const [{ sendSignInLinkToEmail }, auth] = await Promise.all([
      import('firebase/auth'),
      getFirebaseAuth(),
    ]);

    await sendSignInLinkToEmail(auth, email, {
      url: buildActionLinkUrl(redirect),
      handleCodeInApp: true,
    });
  }, []);

  const completeMagicLinkSignIn = useCallback(async (email?: string) => {
    if (typeof window === 'undefined') {
      return null;
    }

    const [{ isSignInWithEmailLink, signInWithEmailLink }, auth] = await Promise.all([
      import('firebase/auth'),
      getFirebaseAuth(),
    ]);

    if (!isSignInWithEmailLink(auth, window.location.href)) {
      return null;
    }

    const resolvedEmail = email ?? window.localStorage.getItem(EMAIL_STORAGE_KEY);

    if (!resolvedEmail) {
      throw new Error('Please provide the email address used to request the magic link.');
    }

    try {
      await signInWithEmailLink(auth, resolvedEmail, window.location.href);
    } finally {
      cleanupAuthQuery();
      window.localStorage.removeItem(EMAIL_STORAGE_KEY);
    }

    const redirect =
      sanitizeRedirect(window.localStorage.getItem(REDIRECT_STORAGE_KEY)) ??
      sanitizeRedirect(new URL(window.location.href).searchParams.get('redirect'));

    if (redirect) {
      window.localStorage.removeItem(REDIRECT_STORAGE_KEY);
    }

    return { redirect: redirect ?? undefined };
  }, []);

  const signInWithGoogle = useCallback(async (options?: { redirect?: string }) => {
    const redirect = sanitizeRedirect(options?.redirect);
    if (typeof window !== 'undefined') {
      if (redirect) {
        window.localStorage.setItem(REDIRECT_STORAGE_KEY, redirect);
      } else {
        window.localStorage.removeItem(REDIRECT_STORAGE_KEY);
      }
    }

    const previous = authManager.getSnapshot();
    authManager.setSnapshot({
      status: 'loading',
      user: previous.user,
      error: undefined,
    });

    const [{ GoogleAuthProvider, signInWithPopup, signInWithRedirect }, auth] = await Promise.all([
      import('firebase/auth'),
      getFirebaseAuth(),
    ]);

    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    const usePopupFirst = shouldUseGooglePopupFirst();

    if (usePopupFirst) {
      try {
        clearGoogleRedirectPending();
        await signInWithPopup(auth, provider);
        return;
      } catch (error) {
        if (!shouldFallbackToGoogleRedirect(error)) {
          const parsedError = mapGoogleAuthError(error);
          authManager.setSnapshot({
            status: 'unauthenticated',
            user: previous.user,
            error: undefined,
          });
          throw parsedError;
        }
      }
    }

    try {
      clearGoogleRedirectPending();
      markGoogleRedirectPending();
      await signInWithRedirect(auth, provider);
    } catch (error) {
      const parsedError = mapGoogleAuthError(error);
      clearGoogleRedirectPending();
      authManager.setSnapshot({
        status: 'unauthenticated',
        user: previous.user,
        error: undefined,
      });
      throw parsedError;
    }
  }, []);

  const signInWithDevSecret = useCallback(
    async (secret: string, options?: { redirect?: string }) => {
      if (!DEV_LOGIN_ENABLED) {
        throw new Error('Development admin login is disabled.');
      }

      if (!secret || !secret.trim()) {
        throw new Error('Development login secret is required.');
      }

      const redirect = sanitizeRedirect(options?.redirect);
      const previous = authManager.getSnapshot();
      authManager.setSnapshot({
        status: 'loading',
        user: previous.user,
        error: undefined,
      });

      try {
        const response = await fetch(`${API_BASE_URL}/auth/dev/login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({ secret }),
        });

        await parseUserResponse(response);
        const profile = await fetchCurrentUserFromSession();

        authManager.setSnapshot({
          status: 'authenticated',
          user: profile,
          error: undefined,
        });
        clearGoogleRedirectPending();

        if (typeof window !== 'undefined') {
          window.localStorage.removeItem(REDIRECT_STORAGE_KEY);
        }

        return redirect;
      } catch (error) {
        const parsedError = error instanceof Error ? error : new Error('Development login failed.');
        authManager.setSnapshot({
          status: 'unauthenticated',
          user: null,
          error: parsedError.message,
        });
        throw parsedError;
      }
    },
    []
  );

  const signInWithDevEmail = useCallback(async (email: string, options?: { redirect?: string }) => {
    if (!DEV_EMAIL_LOGIN_ENABLED) {
      throw new Error('Development email login is disabled.');
    }

    if (!email || !email.trim()) {
      throw new Error('Email is required.');
    }

    const redirect = sanitizeRedirect(options?.redirect);
    const previous = authManager.getSnapshot();
    authManager.setSnapshot({
      status: 'loading',
      user: previous.user,
      error: undefined,
    });

    try {
      const response = await fetch(`${API_BASE_URL}/auth/dev/login-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ email }),
      });

      await parseUserResponse(response);
      const profile = await fetchCurrentUserFromSession();

      authManager.setSnapshot({
        status: 'authenticated',
        user: profile,
        error: undefined,
      });
      clearGoogleRedirectPending();

      if (typeof window !== 'undefined') {
        window.localStorage.removeItem(REDIRECT_STORAGE_KEY);
      }

      return redirect;
    } catch (error) {
      const parsedError =
        error instanceof Error ? error : new Error('Development email login failed.');
      authManager.setSnapshot({
        status: 'unauthenticated',
        user: null,
        error: parsedError.message,
      });
      throw parsedError;
    }
  }, []);

  const refreshUser = useCallback(async () => {
    const auth = await getFirebaseAuth();
    const current = auth.currentUser;
    const previous = authManager.getSnapshot();
    authManager.setSnapshot({
      status: 'loading',
      user: previous.user,
      error: undefined,
    });

    if (!current) {
      if (DEV_SESSION_LOGIN_ENABLED) {
        try {
          const profile = await fetchCurrentUserFromSession();
          authManager.setSnapshot({
            status: 'authenticated',
            user: profile,
            error: undefined,
          });
          return;
        } catch (error) {
          const parsedError =
            error instanceof Error ? error : new Error('Failed to refresh development session');
          if (
            parsedError instanceof HttpError &&
            (parsedError.status === 401 || parsedError.status === 403)
          ) {
            authManager.setSnapshot({ status: 'unauthenticated', user: null, error: undefined });
            return;
          }
          authManager.setSnapshot({
            status: 'unauthenticated',
            user: null,
            error: parsedError.message,
          });
          return;
        }
      }

      authManager.setSnapshot({ status: 'unauthenticated', user: null, error: undefined });
      return;
    }

    try {
      const token = await current.getIdToken();
      let profile: UserProfile;
      try {
        profile = await createSessionWithToken(token);
      } catch (error) {
        if (isRegistrationClosedError(error)) {
          throw error;
        }
        profile = await fetchCurrentUserWithToken(token);
      }
      authManager.setSnapshot({
        status: 'authenticated',
        user: profile,
        error: undefined,
      });
    } catch (error) {
      console.error('Failed to refresh user profile', error);
      const { signOut: firebaseSignOut } = await import('firebase/auth');
      await firebaseSignOut(auth).catch(() => undefined);
      authManager.setSnapshot({
        status: 'unauthenticated',
        user: null,
        error: error instanceof Error ? error.message : 'Authentication failed',
        errorCode: error instanceof HttpError ? error.code : undefined,
      });
    }
  }, []);

  const signOut = useCallback(async () => {
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(EMAIL_STORAGE_KEY);
      window.localStorage.removeItem(REDIRECT_STORAGE_KEY);
    }
    clearGoogleRedirectPending();

    if (DEV_SESSION_LOGIN_ENABLED) {
      await fetch(`${API_BASE_URL}/auth/dev/logout`, {
        method: 'POST',
        credentials: 'include',
      }).catch(() => undefined);
    }

    const [{ signOut: firebaseSignOut }, auth] = await Promise.all([
      import('firebase/auth'),
      getFirebaseAuth(),
    ]);

    await firebaseSignOut(auth).catch(() => undefined);
    authManager.setSnapshot({ status: 'unauthenticated', user: null, error: undefined });
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      ...snapshot,
      isLoading: snapshot.status === 'loading',
      sendMagicLink,
      completeMagicLinkSignIn,
      signInWithGoogle,
      signInWithDevSecret,
      signInWithDevEmail,
      devLoginEnabled: DEV_LOGIN_ENABLED,
      devEmailLoginEnabled: DEV_EMAIL_LOGIN_ENABLED,
      refreshUser,
      signOut,
    }),
    [
      snapshot,
      sendMagicLink,
      completeMagicLinkSignIn,
      signInWithGoogle,
      signInWithDevSecret,
      signInWithDevEmail,
      refreshUser,
      signOut,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
